import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  X, Award, BookOpen, CheckCircle2, Clock, TrendingUp, BarChart3,
  Target, Zap, Activity, ClipboardList, Layers,
} from "lucide-react";

const KINDS = ["lesson", "flashcards", "quiz", "scenario", "summary"];
const KIND_LABELS = {
  lesson: "Lesson",
  flashcards: "Flashcards",
  quiz: "Quiz",
  scenario: "Scenario",
  summary: "Summary",
};

function tierClass(pct) {
  if (pct == null) return "bg-secondary text-muted-foreground";
  if (pct >= 80) return "bg-sage/10 text-sage";
  if (pct >= 60) return "bg-amber-100 text-amber-800";
  return "bg-terracotta/10 text-terracotta";
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function StudentAnalyticsDrawer({ userId, onClose, apiPath }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    // Admin dashboard uses /admin/students/:id/analytics; teacher dashboard
    // uses /staff/students/:id/analytics (same payload, gated by role).
    const path = apiPath || `/admin/students/${userId}/analytics`;
    api.get(path)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, apiPath]);

  // Close on Escape
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#1a1a1a]/40 backdrop-blur-sm flex items-start justify-end"
      onClick={onClose}
      data-testid="analytics-backdrop"
    >
      <div
        className="w-full max-w-3xl h-full bg-bone border-l border-border shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="analytics-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-bone/95 backdrop-blur border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-sage/10 flex items-center justify-center text-sage">
              <BarChart3 className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div>
              <div className="font-display font-semibold text-lg leading-tight">Student analytics</div>
              <div className="text-xs text-muted-foreground">Drill-down view</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="analytics-close"
            className="p-2 rounded-lg hover:bg-secondary text-muted-foreground"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-muted-foreground">Loading analytics…</div>
        )}
        {error && (
          <div className="p-10 text-center text-terracotta" data-testid="analytics-error">{error}</div>
        )}
        {data && !loading && (
          <AnalyticsBody data={data} />
        )}
      </div>
    </div>
  );
}

function AnalyticsBody({ data }) {
  const s = data.student;
  const sum = data.summary;
  return (
    <div className="p-6 space-y-6">
      {/* Student card */}
      <div className="bg-white border border-border rounded-2xl p-5 flex items-center gap-4">
        {s.picture ? (
          <img src={s.picture} alt={s.name} className="w-14 h-14 rounded-full" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-sage/10 text-sage font-semibold text-xl flex items-center justify-center">
            {(s.name || s.email || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-xl truncate">{s.name || "—"}</div>
          <div className="text-sm text-muted-foreground truncate">{s.email}</div>
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
              s.status === "approved" ? "bg-sage/10 text-sage" : "bg-amber-100 text-amber-800"
            }`}>
              {s.status}
            </span>
            <span className="text-muted-foreground">joined {s.joined_at ? new Date(s.joined_at).toLocaleDateString() : "—"}</span>
            {s.devices?.length ? (
              <span className="text-muted-foreground">· {s.devices.length} device{s.devices.length > 1 ? "s" : ""}</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={BookOpen} label="Chapters opened" value={`${sum.chapters_opened}/${sum.total_chapters}`} />
        <StatCard icon={Layers} label="Items viewed" value={sum.items_viewed} />
        <StatCard icon={ClipboardList} label="Quiz attempts" value={sum.quiz_attempts} />
        <StatCard icon={Award} label="Avg. quiz"
          value={sum.avg_quiz_percentage == null ? "—" : `${sum.avg_quiz_percentage}%`}
          tone={sum.avg_quiz_percentage} />
      </div>

      {/* Per-chapter breakdown */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-sage" />
          <div className="font-display font-semibold">Chapter-by-chapter</div>
        </div>
        <div className="space-y-2">
          {data.per_chapter.map((c) => (
            <ChapterRow key={c.chapter_id} c={c} />
          ))}
        </div>
      </div>

      {/* Quiz history */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-sage" />
          <div className="font-display font-semibold">Quiz history</div>
          <span className="text-xs text-muted-foreground">({data.quiz_history.length})</span>
        </div>
        {data.quiz_history.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-6 text-center text-sm text-muted-foreground" data-testid="no-quiz-history">
            No quiz attempts yet.
          </div>
        ) : (
          <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="quiz-history-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-start px-4 py-2.5">Chapter</th>
                  <th className="text-start px-4 py-2.5">Score</th>
                  <th className="text-start px-4 py-2.5">%</th>
                  <th className="text-start px-4 py-2.5">When</th>
                </tr>
              </thead>
              <tbody>
                {data.quiz_history.map((h) => (
                  <tr key={h.result_id || `${h.chapter_id}-${h.completed_at}`} className="border-t border-border">
                    <td className="px-4 py-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Ch {h.chapter_number || "?"}</div>
                      <div className="text-xs">{h.chapter_title || h.chapter_id}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{h.score}/{h.total}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${tierClass(h.percentage)}`}>
                        {h.percentage}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">
                      <span title={h.completed_at ? new Date(h.completed_at).toLocaleString() : ""}>{relTime(h.completed_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  let toneCls = "bg-white border-border";
  if (typeof tone === "number") {
    if (tone >= 80) toneCls = "bg-sage/5 border-sage/30";
    else if (tone >= 60) toneCls = "bg-amber-50 border-amber-200";
    else toneCls = "bg-terracotta/5 border-terracotta/30";
  }
  return (
    <div className={`rounded-2xl border p-4 ${toneCls}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function ChapterRow({ c }) {
  const hasActivity = c.total_views > 0 || c.quiz_attempts > 0;
  const pct = c.quiz_best?.percentage;
  return (
    <div
      className="bg-white border border-border rounded-2xl p-4"
      data-testid={`chapter-row-${c.chapter_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Chapter {c.chapter_number}</div>
          <div className="font-medium">{c.chapter_title}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {pct != null ? (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${tierClass(pct)}`}
              title="Best quiz score"
              data-testid={`chapter-best-${c.chapter_id}`}
            >
              <Award className="w-3 h-3" /> {c.quiz_best.score}/{c.quiz_best.total} · {pct}%
            </span>
          ) : c.quiz_attempts > 0 ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-secondary text-muted-foreground">
              <Zap className="w-3 h-3" /> {c.quiz_attempts} attempt{c.quiz_attempts > 1 ? "s" : ""}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-secondary text-muted-foreground">
              <Target className="w-3 h-3" /> no quiz yet
            </span>
          )}
        </div>
      </div>

      {/* Kind progress bar */}
      <div className="flex items-center gap-2">
        {KINDS.map((k) => {
          const count = c.views[k]?.count || 0;
          const on = count > 0;
          return (
            <div key={k} className="flex-1">
              <div
                className={`h-1.5 rounded-full ${on ? "bg-sage" : "bg-border"}`}
                title={`${KIND_LABELS[k]} · ${count} view${count === 1 ? "" : "s"}`}
              />
              <div className={`text-[10px] mt-1 text-center ${on ? "text-sage font-medium" : "text-muted-foreground"}`}>
                {KIND_LABELS[k].slice(0, 4)}
              </div>
            </div>
          );
        })}
      </div>

      {hasActivity && c.last_activity && (
        <div className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock className="w-3 h-3" /> last activity {relTime(c.last_activity)}
        </div>
      )}
    </div>
  );
}
